import { parseArgs } from "../../features/draft/utils/parseArgs";
import { EditInfo } from "../shared/editInfo";
import { ToolResult } from "../shared/toolResult";
import {
  DeckModel,
  TABLE_SHAPE_TYPE,
  normaliseShapeText,
  shapeCanHoldText,
  shapeSegmentId,
  slideSegmentId,
} from "./deckModel";
import { extractSlideDetail } from "./extractDeck";
import {
  ToolError,
  locateShape,
  locateSlide,
  relocationNote,
  requireShapeAddress,
  requireSlideAddress,
  supportsApiSet,
} from "./locate";

// Turning a tool call into PowerPoint API work.
//
// Every result carries `success` and a human-readable `detail` or `error`, because the
// detail string is what the model reads next: it is the only channel through which this
// layer can tell the model something it did not know when it made the call -- that the
// shape had moved to another slide, that the slide could not be repositioned on this
// host, or what segment ID the thing it just created now answers to.
//
// There is no `bookmarkName` on any result. PowerPoint has no anchor to hand back and no
// revisions model to accept or reject against, so the adapter leaves
// `AssistantAdapter.review` undefined and the chat hides its accept/reject buttons. Edits
// are final the moment they sync.

// ── Arg shapes ───────────────────────────────────────────────────────────────────────

interface ReadSlideArgs {
  segmentId?: string;
}

interface SearchAndReplaceArgs {
  segmentId?: string;
  find?: string;
  replace?: string;
}

interface SetShapeTextArgs {
  segmentId?: string;
  text?: string;
}

interface GeometryArgs {
  left?: number;
  top?: number;
  width?: number;
  height?: number;
}

interface AddTextBoxArgs extends GeometryArgs {
  segmentId?: string;
  text?: string;
}

interface SetShapePositionArgs extends GeometryArgs {
  segmentId?: string;
}

interface DeleteShapeArgs {
  segmentId?: string;
}

interface AddSlideArgs {
  layout?: string;
  position?: number;
}

interface DeleteSlideArgs {
  segmentId?: string;
  slideId?: string;
}

interface SetTableCellTextArgs {
  segmentId?: string;
  row?: number;
  column?: number;
  text?: string;
}

/** Only the measurements the model actually supplied, so the rest keep their values. */
const geometryOptions = (args: GeometryArgs): PowerPoint.ShapeAddOptions => {
  const options: PowerPoint.ShapeAddOptions = {};
  if (typeof args.left === "number") options.left = args.left;
  if (typeof args.top === "number") options.top = args.top;
  if (typeof args.width === "number") options.width = args.width;
  if (typeof args.height === "number") options.height = args.height;
  return options;
};

// ── The tools ────────────────────────────────────────────────────────────────────────

const readSlide = async (args: ReadSlideArgs): Promise<ToolResult> => {
  if (!args.segmentId) throw new ToolError("Missing required argument (segmentId).");
  const slideNumber = requireSlideAddress(args.segmentId);
  const slide = await extractSlideDetail(slideNumber);
  // The detail is JSON because it IS the payload, not a description of one: the chat
  // stringifies the whole ToolResult into the tool message the model reads next.
  return { success: true, detail: JSON.stringify(slide) };
};

const searchAndReplaceInShape = async (args: SearchAndReplaceArgs): Promise<ToolResult> => {
  if (!args.segmentId || !args.find || args.replace === undefined) {
    throw new ToolError("Missing required arguments (segmentId, find, replace).");
  }
  const { slideNumber, shapeId } = requireShapeAddress(args.segmentId);
  const find = args.find;
  const replace = args.replace;
  const segmentId = args.segmentId;

  return PowerPoint.run(async (context) => {
    const located = await locateShape(context, slideNumber, shapeId);
    if (!shapeCanHoldText(located.shapeType)) {
      const alternative =
        located.shapeType === TABLE_SHAPE_TYPE ? " Use set_table_cell_text instead." : "";
      throw new ToolError(
        `Shape "${shapeId}" is a ${located.shapeType} and holds no editable text.${alternative}`
      );
    }

    const frame = located.shape.textFrame;
    frame.load("hasText");
    frame.textRange.load("text");
    await context.sync();

    if (!frame.hasText) {
      throw new ToolError(`Shape "${shapeId}" has no text. Use set_shape_text to give it some.`);
    }

    // Searched against the NORMALISED text so the model's `find` -- copied from a payload
    // in which every break is "\n" -- matches. The resulting index is then applied to the
    // live range, which is sound only because normaliseShapeText never changes the
    // string's length.
    const liveText = normaliseShapeText(frame.textRange.text ?? "");
    const at = liveText.indexOf(find);
    if (at < 0) {
      throw new ToolError(
        `"${find}" does not appear in shape "${shapeId}". Its text is: ${JSON.stringify(liveText)}`
      );
    }
    if (liveText.indexOf(find, at + 1) >= 0) {
      throw new ToolError(
        `"${find}" appears more than once in shape "${shapeId}". Extend it with surrounding words until it is unique.`
      );
    }

    frame.textRange.getSubstring(at, find.length).text = replace;
    await context.sync();

    return {
      success: true,
      detail: `Replaced "${find}" with "${replace}" in ${segmentId}.${relocationNote(located, slideNumber)}`,
    };
  });
};

const setShapeText = async (args: SetShapeTextArgs): Promise<ToolResult> => {
  if (!args.segmentId || args.text === undefined) {
    throw new ToolError("Missing required arguments (segmentId, text).");
  }
  const { slideNumber, shapeId } = requireShapeAddress(args.segmentId);
  const text = args.text;
  const segmentId = args.segmentId;

  return PowerPoint.run(async (context) => {
    const located = await locateShape(context, slideNumber, shapeId);
    if (located.shapeType === TABLE_SHAPE_TYPE) {
      throw new ToolError(
        `Shape "${shapeId}" is a table. Set one cell at a time with set_table_cell_text.`
      );
    }
    if (!shapeCanHoldText(located.shapeType)) {
      throw new ToolError(`Shape "${shapeId}" is a ${located.shapeType} and cannot hold text.`);
    }

    located.shape.textFrame.textRange.text = text;
    await context.sync();

    return {
      success: true,
      detail: `Set the text of ${segmentId}.${relocationNote(located, slideNumber)}`,
    };
  });
};

const addTextBox = async (args: AddTextBoxArgs): Promise<ToolResult> => {
  if (!args.segmentId || args.text === undefined) {
    throw new ToolError("Missing required arguments (segmentId, text).");
  }
  const slideNumber = requireSlideAddress(args.segmentId);
  const text = args.text;
  const options = geometryOptions(args);

  return PowerPoint.run(async (context) => {
    const { slide, slideIndex } = await locateSlide(context, slideNumber);
    const created = slide.shapes.addTextBox(text, options);
    created.load("id");
    await context.sync();

    return {
      success: true,
      detail: `Added a text box to slide ${slideIndex + 1}. Its ID is ${shapeSegmentId(slideIndex, created.id)}.`,
    };
  });
};

const setShapePosition = async (args: SetShapePositionArgs): Promise<ToolResult> => {
  if (!args.segmentId) throw new ToolError("Missing required argument (segmentId).");
  const { slideNumber, shapeId } = requireShapeAddress(args.segmentId);
  const segmentId = args.segmentId;
  const geometry = geometryOptions(args);
  const changes = Object.entries(geometry);
  if (changes.length === 0) {
    throw new ToolError("Nothing to change: give at least one of left, top, width or height.");
  }
  if ((geometry.width ?? 1) <= 0 || (geometry.height ?? 1) <= 0) {
    throw new ToolError("Width and height must be greater than zero.");
  }

  return PowerPoint.run(async (context) => {
    const located = await locateShape(context, slideNumber, shapeId);
    if (geometry.left !== undefined) located.shape.left = geometry.left;
    if (geometry.top !== undefined) located.shape.top = geometry.top;
    if (geometry.width !== undefined) located.shape.width = geometry.width;
    if (geometry.height !== undefined) located.shape.height = geometry.height;
    await context.sync();

    const described = changes.map(([name, value]) => `${name}=${value}`).join(", ");
    return {
      success: true,
      detail: `Set ${described} on ${segmentId}.${relocationNote(located, slideNumber)}`,
    };
  });
};

const deleteShape = async (args: DeleteShapeArgs): Promise<ToolResult> => {
  if (!args.segmentId) throw new ToolError("Missing required argument (segmentId).");
  const { slideNumber, shapeId } = requireShapeAddress(args.segmentId);

  return PowerPoint.run(async (context) => {
    const located = await locateShape(context, slideNumber, shapeId);
    const fromSlide = located.slideIndex + 1;
    located.shape.delete();
    await context.sync();

    return {
      success: true,
      detail: `Deleted shape ${shapeId} from slide ${fromSlide}. This cannot be undone from the pane.`,
    };
  });
};

const addSlide = async (args: AddSlideArgs): Promise<ToolResult> => {
  const requestedLayout = args.layout?.trim();
  const requestedPosition = typeof args.position === "number" ? args.position : undefined;

  return PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");
    const masters = context.presentation.slideMasters;
    masters.load("items/id");
    await context.sync();

    const idsBefore = new Set(slides.items.map((slide) => slide.id));

    let layoutId: string | undefined;
    if (requestedLayout) {
      if (masters.items.length === 0) {
        throw new ToolError(
          "This presentation reports no slide masters, so no layout can be chosen."
        );
      }
      const layouts = masters.items[0].layouts;
      layouts.load("items/id, items/name");
      await context.sync();

      const match = layouts.items.find(
        (layout) => (layout.name ?? "").toLowerCase() === requestedLayout.toLowerCase()
      );
      if (!match) {
        const available = layouts.items.map((layout) => layout.name).join(", ");
        throw new ToolError(
          `No layout named "${requestedLayout}". Available layouts: ${available}.`
        );
      }
      layoutId = match.id;
    }

    slides.add(layoutId ? { layoutId } : undefined);
    await context.sync();

    // `slides.add` returns void and appends to the end, so the new slide has to be found
    // by difference against the ids seen before it. Adding directly at an index is a
    // preview-only API, and `moveTo` needs 1.8 -- which is why `position` below is a best
    // effort that reports itself rather than a promise.
    slides.load("items/id");
    await context.sync();
    const created = slides.items.find((slide) => !idsBefore.has(slide.id));
    if (!created) {
      throw new ToolError("PowerPoint reported no new slide after adding one.");
    }

    let finalIndex = slides.items.length - 1;
    let positionNote = "";
    if (requestedPosition !== undefined) {
      if (supportsApiSet("1.8")) {
        finalIndex = Math.min(Math.max(1, requestedPosition), slides.items.length) - 1;
        created.moveTo(finalIndex);
        await context.sync();
      } else {
        positionNote =
          " This version of PowerPoint cannot move slides (it needs PowerPointApi 1.8), so the slide was left at the end of the deck.";
      }
    }

    const layoutNote = requestedLayout ? ` using layout "${requestedLayout}"` : "";
    return {
      success: true,
      segmentId: slideSegmentId(finalIndex),
      detail: `Added slide ${slideSegmentId(finalIndex)} (slideId "${created.id}")${layoutNote}. Its placeholders are empty -- call read_slide on it to get their segment IDs.${positionNote}`,
    };
  });
};

const deleteSlide = async (args: DeleteSlideArgs): Promise<ToolResult> => {
  if (!args.segmentId || !args.slideId) {
    throw new ToolError(
      "Missing required arguments (segmentId, slideId). Deleting a slide needs its slideId, copied from the presentation state, so that a stale slide number cannot delete the wrong slide."
    );
  }
  const slideNumber = requireSlideAddress(args.segmentId);
  const segmentId = args.segmentId;
  const slideId = args.slideId;

  return PowerPoint.run(async (context) => {
    const located = await locateSlide(context, slideNumber, slideId);
    const movedNote =
      located.slideIndex + 1 === slideNumber
        ? ""
        : ` Note: that slide had moved to position ${located.slideIndex + 1}; it was identified by its slideId, not by ${segmentId}.`;

    located.slide.delete();
    await context.sync();

    return {
      success: true,
      detail: `Deleted slide ${located.slideIndex + 1}. This cannot be undone from the pane.${movedNote}`,
    };
  });
};

const setTableCellText = async (args: SetTableCellTextArgs): Promise<ToolResult> => {
  if (
    !args.segmentId ||
    args.row === undefined ||
    args.column === undefined ||
    args.text === undefined
  ) {
    throw new ToolError("Missing required arguments (segmentId, row, column, text).");
  }
  if (!supportsApiSet("1.8")) {
    throw new ToolError(
      "This version of PowerPoint cannot read or edit table contents (it needs PowerPointApi 1.8). Tell the user the table has to be edited in PowerPoint directly."
    );
  }
  const { slideNumber, shapeId } = requireShapeAddress(args.segmentId);
  const segmentId = args.segmentId;
  const row = args.row;
  const column = args.column;
  const text = args.text;

  return PowerPoint.run(async (context) => {
    const located = await locateShape(context, slideNumber, shapeId);
    if (located.shapeType !== TABLE_SHAPE_TYPE) {
      throw new ToolError(`Shape "${shapeId}" is a ${located.shapeType}, not a table.`);
    }

    const table = located.shape.getTable();
    table.load("rowCount, columnCount");
    await context.sync();

    if (row < 0 || row >= table.rowCount || column < 0 || column >= table.columnCount) {
      throw new ToolError(
        `Cell [${row},${column}] is outside this ${table.rowCount}x${table.columnCount} table.`
      );
    }

    // getCellOrNullObject is the only accessor there is: a cell that sits inside a merged
    // area but is not its top-left cell comes back as a null object rather than an error.
    const cell = table.getCellOrNullObject(row, column);
    await context.sync();
    if (cell.isNullObject) {
      throw new ToolError(
        `Cell [${row},${column}] is inside a merged area but is not its top-left cell, so it has no text of its own. Target the top-left cell of the merge instead.`
      );
    }

    cell.text = text;
    await context.sync();

    return {
      success: true,
      detail: `Set table cell [${row},${column}] of ${segmentId} to "${text}".${relocationNote(located, slideNumber)}`,
    };
  });
};

// ── Dispatch ─────────────────────────────────────────────────────────────────────────

/**
 * Executes a single tool call and returns a normalised result.
 *
 * A plain async function with no React dependencies, matching the Word executor, so it
 * can be reasoned about and tested without the component tree.
 */
export const executeToolCall = async (
  name: string,
  rawArgs: object | string
): Promise<ToolResult> => {
  try {
    switch (name) {
      case "read_slide":
        return await readSlide(parseArgs<ReadSlideArgs>(rawArgs));
      case "search_and_replace_in_shape":
        return await searchAndReplaceInShape(parseArgs<SearchAndReplaceArgs>(rawArgs));
      case "set_shape_text":
        return await setShapeText(parseArgs<SetShapeTextArgs>(rawArgs));
      case "add_text_box":
        return await addTextBox(parseArgs<AddTextBoxArgs>(rawArgs));
      case "set_shape_position":
        return await setShapePosition(parseArgs<SetShapePositionArgs>(rawArgs));
      case "delete_shape":
        return await deleteShape(parseArgs<DeleteShapeArgs>(rawArgs));
      case "add_slide":
        return await addSlide(parseArgs<AddSlideArgs>(rawArgs));
      case "delete_slide":
        return await deleteSlide(parseArgs<DeleteSlideArgs>(rawArgs));
      case "set_table_cell_text":
        return await setTableCellText(parseArgs<SetTableCellTextArgs>(rawArgs));
      default:
        return { success: false, error: `Unknown tool: ${name}` };
    }
  } catch (toolError) {
    // A ToolError is something anticipated and explainable, so it reaches the model as its
    // own message. Anything else is a genuine API or parse failure and is labelled as one,
    // so the model can tell "you asked for the impossible" from "PowerPoint broke".
    if (toolError instanceof ToolError) {
      return { success: false, error: toolError.message };
    }
    return { success: false, error: `PowerPoint API error: ${String(toolError)}` };
  }
};

// ── Edit cards ───────────────────────────────────────────────────────────────────────

const preview = (text: string, limit = 80) =>
  text.length > limit ? `${text.substring(0, limit - 3)}...` : text;

const findShapeText = (deck: DeckModel, segmentId: string): string | undefined => {
  for (const slide of deck.slides) {
    const shape = slide.shapes.find((candidate) => candidate.segmentId === segmentId);
    if (shape) return shape.text;
  }
  return undefined;
};

/**
 * Turns a completed tool call into the rows the chat shows under its reply.
 *
 * These cards are read-only here. Word's carry a bookmark so accept/reject can act on
 * them; PowerPoint has nothing to anchor to and nothing to accept against, so every card
 * stays "pending" for the life of the message and the buttons are hidden. They are still
 * worth building -- they are the only place the user sees a list of what was changed,
 * which matters more, not less, when none of it can be reverted from here.
 */
export const describeDeckEdit = (
  toolName: string,
  rawArgs: object | string,
  result: ToolResult,
  deck: DeckModel
): EditInfo | EditInfo[] | null => {
  if (!result.success) return null;

  const args = parseArgs<Record<string, any>>(rawArgs);
  const segmentId: string = args.segmentId ?? "";
  const card = (label: string, find: string, replace: string): EditInfo => ({
    segmentId,
    label,
    find,
    replace,
    status: "pending",
  });

  switch (toolName) {
    case "search_and_replace_in_shape":
      if (!args.find || args.replace === undefined) return null;
      return card("Making revision", args.find, args.replace);

    case "set_shape_text":
      if (args.text === undefined) return null;
      return card(
        "Replacing text",
        preview(findShapeText(deck, segmentId) ?? "[previous text]"),
        preview(args.text)
      );

    case "add_text_box":
      if (args.text === undefined) return null;
      return card("Adding text box", "[new text box]", preview(args.text));

    case "set_shape_position": {
      const described = ["left", "top", "width", "height"]
        .filter((key) => typeof args[key] === "number")
        .map((key) => `${key}=${args[key]}`)
        .join(", ");
      return card("Moving shape", "[move / resize]", described || "[no change]");
    }

    case "delete_shape":
      return card(
        "Removing shape",
        preview(findShapeText(deck, segmentId) ?? `shape ${segmentId}`),
        "[deleted]"
      );

    case "add_slide": {
      // Not result.detail. That sentence is addressed to the MODEL -- it carries the raw
      // slideId and tells it to call read_slide next -- and it was going on screen in the
      // insertion colour, as though an instruction to the assistant were the contents of
      // the new slide. The card says what was added; the model keeps its own copy.
      //
      // segmentId was a label too ("new slide (Title and Content)"), which parses as no
      // slide at all, so the card offered a "show in document" that silently did nothing.
      // The executor now reports where the slide landed, so the control works.
      const layout = typeof args.layout === "string" ? args.layout.trim() : "";
      const slideNumber = (result.segmentId ?? "").replace(/^s/, "");
      return {
        segmentId: result.segmentId ?? "",
        label: "Adding slide",
        find: "[new slide]",
        replace:
          [slideNumber && `Slide ${slideNumber}`, layout && `${layout} layout`]
            .filter(Boolean)
            .join(" · ") || "Added",
        status: "pending",
      };
    }

    case "delete_slide":
      return card("Removing slide", `slide ${segmentId}`, "[deleted]");

    case "set_table_cell_text":
      return card(
        "Editing table cell",
        `cell [${args.row},${args.column}]`,
        preview(String(args.text ?? ""))
      );

    // read_slide changes nothing, so it earns no card.
    default:
      return null;
  }
};
